'use client';

import { useCallback } from 'react';
import Link from 'next/link';
import { AlertCircle, Loader2 } from 'lucide-react';
import { usePolling } from '@/lib/usePolling';
import { useAuth } from '@/lib/useAuth';
import { hasPermission, hasRole } from '@/lib/auth/types';
import { formatCents } from '@/lib/format';

interface AttentionData {
  service_requests?: { count: number };
  overdue_invoices?: { count: number; total_cents: number };
  manual_charge?: { count: number };
  recent_leads?: { count: number };
  pending_estimates?: { count: number };
  route_orphans?: { count: number };
  overdue_services?: { count: number };
  no_card_customers?: { count: number };
}

interface AttentionItem {
  key: string;
  sentence: string;
  href: string;
}

function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return count === 1 ? singular : pluralForm;
}

export function AttentionPanel() {
  const { user, authEnabled } = useAuth();
  const { data, isLoading, error } = usePolling<AttentionData | null>({
    url: '/api/dashboard/attention',
    intervalMs: 30000,
    transform: useCallback((json: Record<string, unknown>) => json as AttentionData, []),
    initialValue: null,
  });

  if (!data) {
    return (
      <section aria-labelledby="attention-heading" className="mt-6 mb-6">
        <h2 id="attention-heading" className="text-xs font-bold text-zinc-400 uppercase tracking-[0.18em] mb-2">Needs you</h2>
        {error ? (
          <p role="alert" className="flex items-center gap-2 text-sm text-rose-300 py-2">
            <AlertCircle className="h-4 w-4" /> Could not load today&apos;s to-do list. It will retry automatically.
          </p>
        ) : (
          <p role="status" className="flex items-center gap-2 text-sm text-zinc-500 py-2">
            <Loader2 className="h-4 w-4 animate-spin" /> {isLoading ? 'Loading' : 'Waiting to load'} today&apos;s to-do list…
          </p>
        )}
      </section>
    );
  }

  const allowed = (permission: 'view:crm' | 'view:jobs' | 'view:schedule' | 'view:financials') =>
    !authEnabled || (!!user && hasPermission(user.role, permission));
  const isDispatcher = !authEnabled || (!!user && hasRole(user.role, 'dispatcher'));
  const items: AttentionItem[] = [];

  const leadCount = allowed('view:crm') ? data.recent_leads?.count : undefined;
  if (leadCount) items.push({ key: 'leads', sentence: `${leadCount} ${plural(leadCount, 'lead')} in the pipeline`, href: '/marketing/leads' });

  const requestCount = allowed('view:jobs') ? data.service_requests?.count : undefined;
  if (requestCount) items.push({ key: 'requests', sentence: `${requestCount} ${plural(requestCount, 'service request')} waiting for review`, href: '/requests?status=pending' });

  const estimateCount = allowed('view:crm') ? data.pending_estimates?.count : undefined;
  if (estimateCount) items.push({ key: 'estimates', sentence: `${estimateCount} ${plural(estimateCount, 'estimate')} waiting for an answer`, href: '/customers-hub?tab=estimates&status=awaiting_response' });

  const orphanCount = allowed('view:schedule') && isDispatcher ? data.route_orphans?.count : undefined;
  if (orphanCount) items.push({ key: 'orphans', sentence: `${orphanCount} ${plural(orphanCount, 'active property', 'active properties')} missing from Routes`, href: '/dispatch/orphans' });

  const overdueServiceCount = allowed('view:schedule') ? data.overdue_services?.count : undefined;
  if (overdueServiceCount) items.push({ key: 'overdue_services', sentence: `${overdueServiceCount} ${plural(overdueServiceCount, 'service')} overdue`, href: '/sla' });

  if (allowed('view:financials')) {
    const invoiceCount = data.overdue_invoices?.count ?? 0;
    if (invoiceCount > 0) {
      const total = formatCents(data.overdue_invoices?.total_cents ?? 0, { decimals: 0, locale: true });
      items.push({ key: 'overdue', sentence: `${invoiceCount} ${plural(invoiceCount, 'invoice')} overdue, ${total}`, href: '/money?tab=invoices&status=attention_overdue' });
    }

    const manualCount = data.manual_charge?.count ?? 0;
    if (manualCount) items.push({ key: 'manual', sentence: `${manualCount} ${plural(manualCount, 'invoice')} need a manual charge`, href: '/money?tab=invoices&status=manual_charge' });
  }

  const noCardCount = allowed('view:crm') ? data.no_card_customers?.count : undefined;
  if (noCardCount) items.push({ key: 'no_card', sentence: `${noCardCount} ${plural(noCardCount, 'customer')} with no card on file`, href: '/customers-hub?tab=directory&filter=no_card' });

  return (
    <section aria-labelledby="attention-heading" className="mt-6 mb-6">
      <h2 id="attention-heading" className="text-xs font-bold text-zinc-400 uppercase tracking-[0.18em] mb-2">Needs you</h2>
      {error && (
        <p role="status" className="mb-2 text-xs text-amber-300">The last refresh failed; showing the most recently loaded counts.</p>
      )}
      {items.length === 0 ? (
        <p className="border-y border-white/10 py-3 text-sm text-zinc-300">Nothing needs you right now.</p>
      ) : (
        <ul className="divide-y divide-white/10 border-y border-white/10">
          {items.map((item) => (
            <li key={item.key}>
              <Link href={item.href} className="flex min-h-12 items-center py-3 text-sm text-zinc-200 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400">
                <span>{item.sentence}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
